import type { EHeatmapLoadingKey } from '../types';

import { DEFAULT_VIEW_ID } from '@gem-sdk/heatmap-react';
import { create } from 'zustand';

/** Per-viewId loading map: viewId -> loadingKey -> boolean */
export type IHeatmapLoadingMap = Map<EHeatmapLoadingKey, boolean>;

interface IHeatmapLoadingState {
  loadingMap: Map<string, IHeatmapLoadingMap>;
}

interface IHeatmapLoadingActions {
  setLoadingByKey: (key: EHeatmapLoadingKey, isLoading: boolean, viewId?: string) => void;
  getLoadingByKey: (key: EHeatmapLoadingKey, viewId?: string) => boolean;
  copyView: (fromViewId: string, toViewId: string) => void;
  clearView: (viewId: string) => void;
  resetAll: () => void;
}

export interface IHeatmapLoadingStore extends IHeatmapLoadingState, IHeatmapLoadingActions {}

export const useHeatmapLoadingStore = create<IHeatmapLoadingStore>()((set, get) => ({
  loadingMap: new Map(),

  setLoadingByKey: (key, isLoading, viewId = DEFAULT_VIEW_ID) => {
    const newLoadingMap = new Map(get().loadingMap);
    const viewMap = new Map(newLoadingMap.get(viewId) ?? new Map());
    viewMap.set(key, isLoading);
    newLoadingMap.set(viewId, viewMap);
    set({ loadingMap: newLoadingMap });
  },

  getLoadingByKey: (key, viewId = DEFAULT_VIEW_ID) => {
    return get().loadingMap.get(viewId)?.get(key) ?? false;
  },

  copyView: (fromViewId, toViewId) =>
    set((prev) => {
      const newLoadingMap = new Map(prev.loadingMap);
      const fromViewMap = prev.loadingMap.get(fromViewId);
      if (fromViewMap) newLoadingMap.set(toViewId, new Map(fromViewMap));
      return { loadingMap: newLoadingMap };
    }),

  clearView: (viewId) =>
    set((prev) => {
      const newLoadingMap = new Map(prev.loadingMap);
      newLoadingMap.delete(viewId);
      return { loadingMap: newLoadingMap };
    }),

  resetAll: () => set({ loadingMap: new Map() }),
}));
